import { useStore } from "stook";

/**
 * 故障维修「问题描述」信息 store（按维修子项 code 存储）。
 *
 * 每个选中的维修子项（父项 = 故障维修 REPAIR）对应一条：
 * - desc：问题描述文本（必填，≤500）
 * - attachmentIds：该子项上传的图片/视频 fileId 列表（选填，≤9）
 *
 * 异响子项（code=103）的能否重现 / 位置图仍存于 useNoiseInfo，
 * 此处只存异响子项的「问题描述文本 + 附件」，与其它子项统一。
 */
export interface RepairIssueItem {
  desc: string;
  attachmentIds: string[];
  /** 最近一次问题时间（格式 "YYYY-MM-DD HH:mm"，时/分未知以 "--" 标记）；异响子项不使用 */
  lastIssueTime: string;
}

// code(string) → RepairIssueItem
export type RepairIssueMap = Record<string, RepairIssueItem>;

const INIT: RepairIssueMap = {};

const emptyItem = (): RepairIssueItem => ({
  desc: "",
  attachmentIds: [],
  lastIssueTime: "",
});

export default function useRepairIssueInfo() {
  const [issueMap, setIssueMap] = useStore<RepairIssueMap>(
    "RepairIssueInfo",
    INIT
  );

  // 读取某子项（不存在时返回空壳，避免调用方判空）
  const getIssue = (code: number | string): RepairIssueItem =>
    issueMap[String(code)] || emptyItem();

  // 设置某子项的描述
  const setIssueDesc = (code: number | string, desc: string) =>
    setIssueMap((state) => {
      const key = String(code);
      if (!state[key]) state[key] = emptyItem();
      state[key].desc = desc;
    });

  // 设置某子项的附件
  const setIssueAttachments = (
    code: number | string,
    attachmentIds: string[]
  ) =>
    setIssueMap((state) => {
      const key = String(code);
      if (!state[key]) state[key] = emptyItem();
      state[key].attachmentIds = attachmentIds;
    });

  // 设置某子项的最近一次问题时间
  const setIssueLastIssueTime = (
    code: number | string,
    lastIssueTime: string
  ) =>
    setIssueMap((state) => {
      const key = String(code);
      if (!state[key]) state[key] = emptyItem();
      state[key].lastIssueTime = lastIssueTime;
    });

  // 只保留仍被选中的子项 code，清理已取消勾选子项的残留数据
  const pruneIssues = (keepCodes: (number | string)[]) => {
    const keep = keepCodes.map(String);
    setIssueMap((state) => {
      Object.keys(state).forEach((key) => {
        if (!keep.includes(key)) delete state[key];
      });
    });
  };

  // 全量重置（新建预约进入 / 取消所有维修子项）
  const resetIssues = () =>
    setIssueMap(() => ({} as RepairIssueMap));

  // 编辑回显：按后端数据水合（覆盖式写入）
  const hydrateIssues = (map: RepairIssueMap) =>
    setIssueMap(() => {
      const next: RepairIssueMap = {};
      Object.keys(map || {}).forEach((key) => {
        next[key] = {
          desc: map[key]?.desc || "",
          attachmentIds: map[key]?.attachmentIds || [],
          lastIssueTime: map[key]?.lastIssueTime || "",
        };
      });
      return next;
    });

  return {
    issueMap,
    getIssue,
    setIssueDesc,
    setIssueAttachments,
    setIssueLastIssueTime,
    pruneIssues,
    resetIssues,
    hydrateIssues,
  };
}
